import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import AuthImage from '../authImage/AuthImage';
import './ImageViewer.css';

function ImageViewer({ isOpen, onClose, images, initialIndex = 0 }) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const touchStartX = useRef(null);
    const touchEndX = useRef(null);

    const handleTouchStart = (e) => {
        touchStartX.current = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
        touchEndX.current = e.changedTouches[0].screenX;
        handleSwipeGesture();
    };

    const handleSwipeGesture = () => {
        if (!touchStartX.current || !touchEndX.current) return;

        const deltaX = touchStartX.current - touchEndX.current;

        // Threshold to avoid accidental swipes
        if (Math.abs(deltaX) > 50) {
            if (deltaX > 0) {
                handleNext();
            } else {
                handlePrevious();
            }
        }

        // Reset
        touchStartX.current = null;
        touchEndX.current = null;
    };

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
        }
    }, [isOpen, initialIndex]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentIndex]);

    if (!isOpen || !images || images.length === 0) return null;

    const handleNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handlePrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const currentImage = images[currentIndex];

    return (
        <div className="image-viewer-overlay" onClick={handleOverlayClick}
             onTouchStart={handleTouchStart}
             onTouchEnd={handleTouchEnd}>
            <div className="image-viewer-container">
                {/* Close button */}
                <button className="image-viewer-close" onClick={onClose}>
                    <FiX />
                </button>

                {/* Navigation buttons */}
                {images.length > 1 && (
                    <>
                        <button
                            className="image-viewer-nav image-viewer-prev"
                            onClick={handlePrevious}
                        >
                            <FiChevronLeft />
                        </button>
                        <button
                            className="image-viewer-nav image-viewer-next"
                            onClick={handleNext}
                        >
                            <FiChevronRight />
                        </button>
                    </>
                )}

                {/* Image container */}
                <div className="image-viewer-content">
                    <AuthImage
                        src={currentImage.url}
                        alt={currentImage.fileName || `Image ${currentIndex + 1}`}
                        className="image-viewer-image"
                    />
                </div>

                {/* Image info */}
                <div className="image-viewer-info">
                    <span className="image-viewer-filename">
                        {currentImage.fileName || `Image ${currentIndex + 1}`}
                    </span>
                    {images.length > 1 && (
                        <span className="image-viewer-counter">
                            {currentIndex + 1} / {images.length}
                        </span>
                    )}
                </div>

                {/* Thumbnail strip for many images */}
                {images.length > 1 && (
                    <div className="image-viewer-thumbnails">
                        {images.map((image, index) => (
                            <div
                                key={image.id}
                                className={`image-viewer-thumbnail ${
                                    index === currentIndex ? 'active' : ''
                                }`}
                                onClick={() => setCurrentIndex(index)}
                            >
                                <AuthImage
                                    src={image.url}
                                    alt={image.fileName || `Thumbnail ${index + 1}`}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ImageViewer;